import { useMutation } from '@tanstack/react-query'
import { analyticsApi } from 'src/services/analyticsApi'

export interface CampaignComparisonData {
  id: string
  mentions: number
  name: string
  sentiments: {
    negative: number
    neutral: number
    positive: number
  }
  sources: {
    booking: number
    facebook: number
    google: number
    googleReviews: number
    instagram: number
    linkedin: number
    newsBlogs: number
    reddit: number
    tiktok: number
    tripadvisor: number
    twitter: number
    websites: number
    yandex: number
    youtube: number
  }
}

export interface ComparisonStats {
  leastMentionsCampaign: {
    count: number
    id: string
    name: string
  } | null
  leastPopularSource: {
    count: number
    name: string
  } | null
  mostMentionsCampaign: {
    count: number
    id: string
    name: string
  } | null
  mostNegativeCampaign: {
    id: string
    name: string
    percentage: number
  } | null
  mostPopularSource: {
    count: number
    name: string
  } | null
  mostPositiveCampaign: {
    id: string
    name: string
    percentage: number
  } | null
  totalMentions: number
}

export interface CampaignCompareRequest {
  benchmarkCampaignId?: string
  campaignIds: string[]
  fromDate?: string
  metrics: ('mentions' | 'sources' | 'sentiments')[]
  toDate?: string
}

export interface SavedPreferences {
  benchmarkCampaignId?: string | null
  campaignIds?: string[]
  dateRange?: {
    fromDate?: string
    toDate?: string
  } | null
  lastUpdated?: string
  metrics?: string[]
}

export interface CampaignCompareResponse {
  data: {
    campaigns: CampaignComparisonData[]
    savedPreferences?: SavedPreferences | null
    stats: ComparisonStats
  }
  success: boolean
}

const compareCampaigns = async (
  params: CampaignCompareRequest
): Promise<CampaignCompareResponse> => {
  // Separate dates from body params
  const { fromDate, toDate, ...bodyParams } = params

  // Build query string for dates
  const queryParams = new URLSearchParams()
  if (fromDate) {
    queryParams.append('fromDate', fromDate)
  }
  if (toDate) {
    queryParams.append('toDate', toDate)
  }

  const queryString = queryParams.toString()
  const url = `/v1_private/campaign/compare${
    queryString ? `?${queryString}` : ''
  }`

  // Use the configured analytics API client
  const { data } = await analyticsApi().post(url, bodyParams)

  return data
}

export const useCompareCampaignsQuery = () => {
  return useMutation({
    mutationFn: compareCampaigns,
  })
}
